import { ref, watch } from 'vue';
import type { ViewMode } from './types';

// ITDEVELOP-3170: a user's display choices for one table — card/table view and
// hidden columns — persisted so they survive navigation and reloads. Keyed per
// table (usually the route path), so trimming the Users table never affects
// Visitors.

const STORAGE_PREFIX = 'table-view-prefs:';

interface StoredPrefs {
    view?: ViewMode;
    hidden?: string[];
}

function read(key: string): StoredPrefs {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_PREFIX + key) ?? '{}');
    } catch {
        return {};
    }
}

export function useTableViewPrefs(key: string) {
    const stored = read(key);

    // null = the user has not chosen yet — the table falls back to its
    // developer default (mobileMode): a table on phones, or cards for the
    // pages that opt in, until the user expresses a preference.
    const view = ref<ViewMode | null>(stored.view === 'cards' || stored.view === 'table' ? stored.view : null);
    const hiddenColumns = ref<string[]>(
        Array.isArray(stored.hidden) ? stored.hidden.filter((h) => typeof h === 'string') : [],
    );

    watch(
        [view, hiddenColumns],
        ([v, hidden]) => {
            localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({ view: v ?? undefined, hidden }));
        },
        { deep: true },
    );

    function toggleColumn(columnId: string) {
        hiddenColumns.value = hiddenColumns.value.includes(columnId)
            ? hiddenColumns.value.filter((c) => c !== columnId)
            : [...hiddenColumns.value, columnId];
    }

    function reset() {
        view.value = null;
        hiddenColumns.value = [];
    }

    return { view, hiddenColumns, toggleColumn, reset };
}
